import {
  makeObservable,
  observable,
  action,
  computed,
  runInAction,
} from 'mobx';
// eslint-disable-next-line import/no-cycle
import api from '../api';
import { SUPER_ADMIN, ADMIN, USER } from '../constants/users';

import {
  API_SCAN_CONFIG_US_DATABASE,
  API_SCAN_CONFIG_FEDORA,
  API_SCAN_CONFIG_DEBIAN,
  API_SCAN_CONFIG_VULN,
  API_SCAN_CONFIG_LITIGATORS,
  API_SCAN_CONFIG_COMPLEX_LICENSING,
} from '../constants/reports';
import { convertPropertiesToCamelCase } from '../utils/shared';

class userStore {
  id = '';

  name = '';

  role = '';

  image_thumb = '';

  image_medium = '';

  email = '';

  appVersion = '';

  access_token = '';

  token_type = '';

  expires_in = '';

  refresh_token = '';

  view_mode = '';

  page_limit = '';

  isLoading = true;

  use_ldap = true;

  scanConfig = {
    usDatabase: true,
    fedora: true,
    debian: true,
    vuln: true,
    litigators: true,
    complexLicensing: true,
    isLoading: false,
  };

  required_setting = '';

  groupsCount = {};

  policysCount = {};

  main_page = '';

  currentPrecisionLevel = null;

  currentPrecisionLevelActive = 0;

  new_notification = false;

  use_leakage = false;

  localeParams = '';

  isUpdateScannedFileList = false;

  isUpdateComponentList = false;

  use_crypto = false;

  transientNavigationState = null;

  constructor() {
    makeObservable(this, {
      id: observable,
      name: observable,
      role: observable,
      image_thumb: observable,
      image_medium: observable,
      email: observable,
      appVersion: observable,
      access_token: observable,
      token_type: observable,
      expires_in: observable,
      refresh_token: observable,
      view_mode: observable,
      page_limit: observable,
      isLoading: observable,
      use_ldap: observable,
      scanConfig: observable,
      required_setting: observable,
      groupsCount: observable,
      policysCount: observable,
      main_page: observable,
      currentPrecisionLevel: observable,
      currentPrecisionLevelActive: observable,
      new_notification: observable,
      use_leakage: observable,
      localeParams: observable,
      isUpdateScannedFileList: observable,
      isUpdateComponentList: observable,
      use_crypto: observable,
      transientNavigationState: observable,
      securityScanConfig: computed,
      complianceScanConfig: computed,
      isSuperAdmin: computed,
      isAdmin: computed,
      isAdminOnly: computed,
      isUser: computed,
      displayName: computed,
      getUserScanConfig: action,
      changeRoll: action,
      changeLdap: action,
      toggleScanConfig: action,
      toggleSecurityScanConfig: action,
      toggleComplianceScanConfig: action,
      setName: action,
      setUserProfile: action,
      setSession: action,
      setRequiredSetting: action,
      getsGroupCount: action,
      getsPolicyCount: action,
      setPrecisionLevel: action,
      setCurrentConfidenceLevel: action,
      initPrecisionLevel: action,
      setLocaleParams: action,
      setUpdateSummaryValue: action,
      setUpdateComponentSummaryValue: action,
      setTransientNavigationState: action,
      clearTransientNavigationState: action,
      setUseCrypto: action,
    });
  }

  getUserScanConfig() {
    api.fetchUserScanConfig(this.id).then((response) => {
      const config = response.response;
      this.scanConfig = {
        usDatabase: config[API_SCAN_CONFIG_US_DATABASE],
        fedora: config[API_SCAN_CONFIG_FEDORA],
        debian: config[API_SCAN_CONFIG_DEBIAN],
        vuln: config[API_SCAN_CONFIG_VULN],
        litigators: config[API_SCAN_CONFIG_LITIGATORS],
        complexLicensing: config[API_SCAN_CONFIG_COMPLEX_LICENSING],
        isLoading: false,
      };
    });
  }

  changeRoll(dara) {
    api.changeRoll(dara).then(() => {
      //console.log(response);
    });
  }

  changeLdap(dara) {
    api.changeLdap(dara).then(() => {
      //console.log(response);
    });
  }

  get securityScanConfig() {
    const { usDatabase, fedora, debian, vuln } = this.scanConfig;

    return usDatabase || fedora || debian || vuln;
  }

  get complianceScanConfig() {
    const { litigators, complexLicensing } = this.scanConfig;

    return litigators || complexLicensing;
  }

  get isSuperAdmin() {
    return this.role === SUPER_ADMIN;
  }

  get isAdmin() {
    return this.role === SUPER_ADMIN || this.role === ADMIN;
  }

  get isAdminOnly() {
    return this.role === ADMIN;
  }

  get isUser() {
    return this.role === USER || this.role === ADMIN;
  }

  get displayName() {
    return `${this.name} ${this.lastName}`;
  }

  toggleScanConfig(name) {
    this.scanConfig[name] = !this.scanConfig[name];
  }

  toggleSecurityScanConfig() {
    const isChecked = !this.securityScanConfig;

    this.scanConfig.usDatabase = isChecked;
    this.scanConfig.fedora = isChecked;
    this.scanConfig.debian = isChecked;
    this.scanConfig.vuln = isChecked;
  }

  toggleComplianceScanConfig() {
    const isChecked = !this.complianceScanConfig;

    this.scanConfig.litigators = isChecked;
    this.scanConfig.complexLicensing = isChecked;
  }

  setName(name) {
    this.name = name;
  }

  setUserProfile(profile) {
    this.id = profile.id;
    this.name = profile.name;
    this.company = profile.company;
    this.image_thumb = `/1/users/me/profile_image/${profile.id}?`
      .concat('?')
      .concat(new Date().getTime());
    this.image_medium = `/1/users/me/profile_image/${profile.id}?`
      .concat('?')
      .concat(new Date().getTime());
    this.email = profile.email;
    this.role = profile.role;
    this.appVersion = profile.app_version;
    this.use_ldap = profile.use_ldap;
    this.main_page = profile.main_page;
    this.view_mode = profile.view_mode;
    this.page_limit = profile.page_limit;
    this.new_notification = profile.new_notification;
    this.use_leakage = profile.use_leakage;
    this.use_crypto = profile.use_crypto;
    localStorage.setItem('use_leakage', profile.use_leakage);
    localStorage.setItem('main_page', profile.main_page);
    localStorage.setItem('use_crypto', profile.use_crypto);
    localStorage.setItem('page_limit', profile.page_limit);
  }

  setSession = async (session) => {
    this.isLoading = true;
    this.access_token = session.access_token;
    this.token_type = session.token_type;
    localStorage.setItem('session', JSON.stringify(session));
    await api.getUserProfile(this.access_token).then((response) => {
      runInAction(() => {
        this.setUserProfile(response.response);
        this.isLoading = false;
      });
    });

    //return api.getUserProfile(this.access_token).then((response) => {
    //this.setUserProfile(response.response);
    //this.isLoading = false;
    //});
  };

  setRequiredSetting(data) {
    this.required_setting = data;
  }

  getsGroupCount = async () => {
    await api.getsGroupCount().then((response) => {
      runInAction(() => {
        this.groupsCount = convertPropertiesToCamelCase(response.response);
      });
    });
  };

  getsPolicyCount = async () => {
    await api.getsPolicyCount().then((response) => {
      runInAction(() => {
        this.policysCount = convertPropertiesToCamelCase(response.response);
      });
    });
  };

  setCurrentConfidenceLevel(value) {
    this.currentPrecisionLevel = value;
  }

  setPrecisionLevel(value) {
    this.currentPrecisionLevel = value;
    this.currentPrecisionLevelActive++;
  }

  initPrecisionLevel() {
    this.currentPrecisionLevel = null;
    this.currentPrecisionLevelActive = 0;
  }

  getValidSession() {
    let storageSession = {};
    if (localStorage.getItem('session')) {
      storageSession = JSON.parse(localStorage.getItem('session'));
      /*
      const now = Date.now();
      const expiration = (storageSession.created_at + storageSession.expires_in) * 1000;
      if (now > expiration) {
        return api.refreshToken(storageSession.refresh_token).then((response) => {
          if (!response || response.error) return Promise.reject();
        });
      }*/
      return this.setSession(storageSession);
    }
    return Promise.reject();
  }

  signOut() {
    const data = {
      action_from: 'manual_sign_out',
    };
    return api
      .signOut(data)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        console.log(err);
      });
  }

  setLocaleParams = (value) => {
    this.localeParams = value;
  };

  setUpdateSummaryValue = (value) => {
    this.isUpdateScannedFileList = value;
  };

  setUpdateComponentSummaryValue = (value) => {
    this.isUpdateComponentList = value;
  };

  setTransientNavigationState = (state) => {
    this.transientNavigationState = state;
  };

  clearTransientNavigationState = () => {
    this.transientNavigationState = null;
  };

  setUseCrypto = (value) => {
    this.use_crypto = value;
  };
}

export default userStore;
